import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { EyeIcon, EyeClosedIcon } from 'lucide-react';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { LogoHasilHoki } from '../assets';
import { navPath, UserRole } from '../constant';

type UserRoleType = keyof typeof UserRole;

interface LoginCredentials {
  username: string;
  password: string;
}

interface LoginResponse {
  data: {
    username: string;
    role: UserRoleType;
  };
  message?: string;
}

const Login = () => {
  const navigate = useNavigate();

  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: '',
    password: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const localStorageName = {
      role: 'role',
      username: 'username',
    };

    setIsLoading(true);
    setErrorMessage('');

    if (!credentials.username.trim()) {
      setErrorMessage('Please input your username.');
      setIsLoading(false);
      return;
    }

    if (!credentials.password) {
      setErrorMessage('Please input proper password');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/v1/user/login', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = (await response.json()) as LoginResponse;

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      localStorage.setItem(localStorageName.role, data.data.role);
      localStorage.setItem(localStorageName.username, data.data.username);

      navigate(navPath.dashboard.monitor.admin);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred during login.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>SEO BAR BAR - Login</title>
        <meta name='description' content='Login to access your SEO BAR BAR dashboard' />
      </Helmet>

      <div className='bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen flex justify-center items-center p-4'>
        <div className='w-full max-w-md space-y-8'>
          <div className='flex flex-col items-center'>
            <img
              className='h-24 sm:h-28 mb-3 transition-transform hover:scale-105 duration-300'
              src={LogoHasilHoki.default}
              alt='Hasil-Hoki'
            />
          </div>

          <Card className='border-primary/10 shadow-xl'>
            <CardHeader className='space-y-1'>
              <CardTitle className='text-2xl text-center font-bold'>Sign in</CardTitle>
              <CardDescription className='text-center'>Enter your credentials to access your account</CardDescription>
            </CardHeader>
            <CardContent>
              {errorMessage && (
                <Alert variant='destructive' className='mb-4'>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='username'>Username</Label>
                  <Input
                    id='username'
                    name='username'
                    type='text'
                    placeholder='Enter your username'
                    value={credentials.username}
                    onChange={handleInputChange}
                    required
                    autoComplete='username'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='password'>Password</Label>
                  <div className='relative'>
                    <Input
                      id='password'
                      name='password'
                      type={showPassword ? 'text' : 'password'}
                      placeholder='Enter your password'
                      value={credentials.password}
                      onChange={handleInputChange}
                      required
                      autoComplete='current-password'
                    />
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      className='absolute right-0 top-0 h-full px-3 py-2 text-muted-foreground hover:text-foreground'
                      onClick={togglePasswordVisibility}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeIcon className='h-4 w-4' /> : <EyeClosedIcon className='h-4 w-4' />}
                    </Button>
                  </div>
                </div>

                <CardFooter className='px-0 pt-4 pb-0'>
                  <Button type='submit' className='w-full' disabled={isLoading}>
                    {isLoading ? (
                      <div className='flex items-center'>
                        <svg
                          className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                        >
                          <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                          <path
                            className='opacity-75'
                            fill='currentColor'
                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                          ></path>
                        </svg>
                        Signing in...
                      </div>
                    ) : (
                      'Sign in'
                    )}
                  </Button>
                </CardFooter>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Login;
